import React, { useMemo, useRef } from "react";

/**
 * Reusable OTP input cluster.
 * - Fully keyboard accessible (arrows, backspace/delete).
 * - Supports paste of multiple digits.
 * - Controlled via `value` (string) and `onChange`.
 */
export default function OtpInput({
  length = 6,
  value,
  onChange,
  className = "",
  inputClassName = "",
}: {
  length?: number;
  value: string;                     // e.g., "12" or "123456"
  onChange: (next: string) => void;  // called with the new combined value
  className?: string;                // wrapper classes
  inputClassName?: string;           // input classes
}) {
  const vals = useMemo(() => {
    const arr = Array.from({ length }, (_, i) => value[i] ?? "");
    return arr;
  }, [value, length]);

  // Pre-size the refs so indices exist
  const inputsRef = useRef<(HTMLInputElement | null)[]>(
    Array(length).fill(null)
  );

  const focusIndex = (idx: number) => inputsRef.current[idx]?.focus();

  const setCharAt = (idx: number, ch: string) => {
    const arr = [...vals];
    arr[idx] = ch;
    onChange(arr.join("").slice(0, length));
  };

  const handleChange = (idx: number, v: string) => {
    const digit = v.replace(/\D/g, "");
    // accept only one character in the box
    setCharAt(idx, digit.slice(-1) || "");
    if (digit && idx < length - 1) focusIndex(idx + 1);
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      if (!vals[idx] && idx > 0) {
        // move left and clear previous
        const arr = [...vals];
        arr[idx - 1] = "";
        onChange(arr.join(""));
        focusIndex(idx - 1);
        e.preventDefault();
        return;
      }
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      focusIndex(idx - 1);
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowRight" && idx < length - 1) {
      focusIndex(idx + 1);
      e.preventDefault();
      return;
    }
  };

  const handlePaste = (idx: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    const arr = [...vals];
    for (let i = 0; i < length - idx && i < text.length; i++) {
      arr[idx + i] = text[i];
    }
    onChange(arr.join(""));
    focusIndex(Math.min(idx + text.length, length - 1));
  };

  return (
    <div className={`flex items-center justify-center gap-2 sm:gap-3 ${className}`}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el; }}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={vals[i]}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          className={
            inputClassName ||
            "h-11 w-10 sm:h-12 sm:w-12 rounded-xl border border-gray-300 text-center text-base sm:text-lg font-semibold tracking-widest text-gray-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          }
        />
      ))}
    </div>
  );
}
